import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays, MapPin, CalendarClock, Cloud } from "lucide-react";
import { log } from "../../../lib/log";

const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 7h → 20h
const ROW_H = 48;
const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const startOfWeek = (d) => {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  return x;
};
const pad = (n) => String(n).padStart(2, "0");
const localIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const STYLE = {
  rdv: "bg-brand text-white border-brand-dark",
  rdv_confirme: "bg-emerald-600 text-white border-emerald-700",
  planifiee: "bg-emerald-100 text-emerald-900 border-emerald-300",
  en_cours: "bg-amber-100 text-amber-900 border-amber-300",
  terminee: "bg-neutral-200 text-neutral-600 border-neutral-300",
  reassign: "bg-red-50 text-red-800 border-red-400 border-dashed",
  dolibarr: "bg-white text-neutral-700 border-dashed border-neutral-400",
};

const absentOn = (absences, d) => {
  const iso = localIso(d).slice(0, 10);
  return absences.filter((a) => a.date_debut <= iso && (a.date_fin || a.date_debut) >= iso);
};

export const buildEvents = (appts, interventions, doliEvents) => {
  const mirrored = new Set([...appts, ...interventions].map((x) => String(x.dolibarr_event_id || "")).filter(Boolean));
  const ev = [];
  appts.forEach((a) => a.status !== "annule" && ev.push({
    key: `rdv-${a.id}`, kind: "rdv", id: a.id, start: new Date(a.date), hours: 1, title: a.titre,
    sub: a.major_nom ? `Major : ${a.major_nom}` : "", style: STYLE[a.status === "confirme" ? "rdv_confirme" : "rdv"], draggable: true, raw: a,
  }));
  interventions.forEach((i) => ev.push({
    key: `intv-${i.id}`, kind: "intervention", id: i.id, start: new Date(i.date), hours: Number(i.duree_h) || 2, title: i.client_nom,
    sub: i.needs_reassign ? "⚠️ À réaffecter" : [i.prestation, i.collaborateur_nom].filter(Boolean).join(" · "),
    style: i.needs_reassign ? STYLE.reassign : (STYLE[i.status] || STYLE.planifiee),
    draggable: i.status === "planifiee", raw: i,
  }));
  doliEvents.forEach((e) => {
    if (!e.datep || mirrored.has(String(e.id))) return;
    ev.push({ key: `doli-${e.id}`, kind: "dolibarr", id: e.id, start: new Date(Number(e.datep) * 1000), hours: 1, title: e.label || "Événement Dolibarr", sub: "Dolibarr", style: STYLE.dolibarr, draggable: false, raw: e });
  });
  return ev.filter((e) => !isNaN(e.start));
};

const EventChip = ({ e, onSelect, onDragStart }) => {
  const top = (e.start.getHours() + e.start.getMinutes() / 60 - 7) * ROW_H;
  const Icon = e.kind === "intervention" ? MapPin : e.kind === "dolibarr" ? Cloud : CalendarClock;
  return (
    <button
      draggable={e.draggable}
      onDragStart={(ev) => onDragStart(ev, e)}
      onClick={() => onSelect(e)}
      data-testid={`agenda-event-${e.kind}-${e.id}`}
      style={{ top, height: Math.max(ROW_H * e.hours - 4, 28) }}
      className={`absolute left-1 right-1 rounded-md border px-1.5 py-1 text-left text-[11px] leading-tight overflow-hidden shadow-sm transition-transform hover:scale-[1.02] ${e.style} ${e.draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
    >
      <span className="flex items-center gap-1 font-semibold truncate"><Icon size={10} className="shrink-0" /> {pad(e.start.getHours())}:{pad(e.start.getMinutes())} {e.title}</span>
      {e.sub && <span className="block truncate opacity-80">{e.sub}</span>}
    </button>
  );
};

const AgendaCalendar = ({ events, onMove, onSelect, onCreate, holidays = {}, onWeekChange, absences = [] }) => {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [overCell, setOverCell] = useState(null);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; }), [weekStart]);
  const eventsByDay = useMemo(() => days.map((d) => events.filter((e) => sameDay(e.start, d))), [days, events]);
  const today = new Date();
  const shift = (n) => setWeekStart((w) => { const d = new Date(w); d.setDate(d.getDate() + n * 7); return d; });
  useEffect(() => { onWeekChange && onWeekChange(weekStart); }, [weekStart, onWeekChange]);
  const holidayOf = (d) => holidays[localIso(d).slice(0, 10)];

  const onDragStart = (ev, e) => {
    ev.dataTransfer.setData("text/plain", JSON.stringify({ kind: e.kind, id: e.id }));
    ev.dataTransfer.effectAllowed = "move";
  };
  const onDrop = (ev, day, hour) => {
    ev.preventDefault();
    setOverCell(null);
    try {
      const { kind, id } = JSON.parse(ev.dataTransfer.getData("text/plain"));
      const rect = ev.currentTarget.getBoundingClientRect();
      const minutes = ev.clientY - rect.top > rect.height / 2 ? 30 : 0;
      const d = new Date(day); d.setHours(hour, minutes, 0, 0);
      onMove(kind, id, localIso(d));
    } catch (error) { log.error("drop:", error); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden" data-testid="agenda-calendar">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="p-1.5 rounded hover:bg-neutral-100" data-testid="agenda-prev-week"><ChevronLeft size={16} /></button>
          <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="text-sm font-semibold text-neutral-700 hover:text-brand flex items-center gap-1.5" data-testid="agenda-today"><CalendarDays size={15} /> Aujourd'hui</button>
          <button onClick={() => shift(1)} className="p-1.5 rounded hover:bg-neutral-100" data-testid="agenda-next-week"><ChevronRight size={16} /></button>
        </div>
        <p className="text-sm font-semibold text-neutral-800" data-testid="agenda-week-label">
          Semaine du {days[0].toLocaleDateString("fr-FR", { day: "numeric", month: "long" })} au {days[6].toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
        </p>
        <p className="text-xs text-neutral-500 hidden xl:block max-w-xs text-right">Cliquez sur une case vide pour créer un RDV ou une intervention ; glissez-déposez pour replanifier (synchro Dolibarr).</p>
      </div>
      <div className="overflow-x-auto">
        <div className="grid min-w-[900px]" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
          <div />
          {days.map((d) => (
            <div key={d.toISOString()} className={`text-center py-2 border-b border-l border-neutral-100 text-xs font-semibold ${holidayOf(d) ? "bg-amber-50" : ""} ${sameDay(d, today) ? "text-brand" : "text-neutral-600"}`}>
              {DAY_LABELS[(d.getDay() + 6) % 7]} <span className={`ml-1 inline-flex w-6 h-6 items-center justify-center rounded-full ${sameDay(d, today) ? "bg-brand text-white" : ""}`}>{d.getDate()}</span>
              {holidayOf(d) && <span className="block mt-0.5 text-[10px] font-bold text-amber-700 truncate" data-testid={`agenda-holiday-${localIso(d).slice(0, 10)}`}>Férié · {holidayOf(d)}</span>}
              {absentOn(absences, d).length > 0 && (
                <span className="block mt-0.5 text-[10px] font-semibold text-red-600 truncate" title={absentOn(absences, d).map((a) => `${a.nom} — ${a.type_label}`).join("\n")} data-testid={`agenda-absent-${localIso(d).slice(0, 10)}`}>
                  Absent : {absentOn(absences, d).map((a) => a.nom).join(", ")}
                </span>
              )}
            </div>
          ))}
          <div>
            {HOURS.map((h) => <div key={h} style={{ height: ROW_H }} className="text-[11px] text-neutral-400 text-right pr-2 relative -top-1.5">{pad(h)}:00</div>)}
          </div>
          {days.map((d, di) => (
            <div key={d.toISOString()} className={`relative border-l border-neutral-100 ${holidayOf(d) ? "bg-amber-50/60" : ""}`}>
              {HOURS.map((h) => {
                const active = overCell === `${d.getDate()}-${h}`;
                return (
                  <div key={h} style={{ height: ROW_H }} data-testid={`agenda-cell-${d.getDate()}-${h}`}
                    onDragOver={(ev) => { ev.preventDefault(); setOverCell(`${d.getDate()}-${h}`); }}
                    onDragLeave={() => setOverCell(null)}
                    onDrop={(ev) => onDrop(ev, d, h)}
                    onClick={(ev) => {
                      if (!onCreate) return;
                      const rect = ev.currentTarget.getBoundingClientRect();
                      const dt = new Date(d); dt.setHours(h, ev.clientY - rect.top > rect.height / 2 ? 30 : 0, 0, 0);
                      onCreate(localIso(dt));
                    }}
                    title="Cliquer pour créer un RDV ou une intervention"
                    className={`border-b border-neutral-100 transition-colors ${active ? "bg-brand/10" : ""} ${onCreate ? "hover:bg-neutral-50 cursor-cell" : ""}`} />
                );
              })}
              {eventsByDay[di].map((e) => <EventChip key={e.key} e={e} onSelect={onSelect} onDragStart={onDragStart} />)}
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-3 px-4 py-2 border-t border-neutral-100 text-[11px] text-neutral-600">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-brand" /> RDV</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-600" /> RDV confirmé</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" /> Intervention planifiée</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300" /> En cours</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-neutral-200" /> Terminée</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-dashed border-red-400" /> À réaffecter (easer absent)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-dashed border-neutral-400" /> Autre événement Dolibarr</span>
      </div>
    </div>
  );
};

export default AgendaCalendar;
